import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../blog/components/BlogBreadcrumbs";
import { SeoMetaForm } from "../blog/components/SeoMetaForm";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import CustomModal from "../../common/customModal/CustomModal";
import { fetchSeoMeta, upsertSeoMeta, deleteSeoMeta } from "./seoSlice";
import { inputSx } from "../blog/blogTheme";

const EMPTY_SEO = { pageKey: "", metaTitle: "", metaDescription: "", canonicalUrl: "", focusKeyword: "", keywords: [], ogTitle: "", ogDescription: "", ogImage: "", twitterCard: "summary", twitterTitle: "", twitterDescription: "", twitterCreator: "", noIndex: false, noFollow: false };

export default function SeoSettings() {
  const dispatch = useDispatch();
  const { records, loading, error } = useSelector((state) => state.seo);
  const sorted = [...records].sort((a, b) => a.pageKey.localeCompare(b.pageKey));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_SEO);
  const [deletingId, setDeletingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { dispatch(fetchSeoMeta()); }, [dispatch]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_SEO); setDrawerOpen(true); };
  const openEdit   = (rec) => {
    setEditing(rec);
    setForm({ ...EMPTY_SEO, ...rec });
    setDrawerOpen(true);
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const { pageKey, ...rest } = form;
    dispatch(upsertSeoMeta({ pageKey, data: rest }));
    setDrawerOpen(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({ title: "Delete SEO Record", description: "Are you sure you want to delete this SEO record?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteSeoMeta(id));
    setDeletingId(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "SEO Settings" }]} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>SEO Settings</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
            color: "#fff",
            borderRadius: "29px",
            boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
            fontFamily: "Heebo",
            px: 3,
            "&:hover": { opacity: 0.9 },
          }}
        >
          Add Page
        </Button>
      </Box>

      {loading && <CircularProgress size={24} sx={{ mb: 2, color: "#065F46" }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <List disablePadding>
        {sorted.map((rec, idx) => (
          <React.Fragment key={rec._id}>
            {idx > 0 && <Divider />}
            <ListItem
              secondaryAction={
                <Box>
                  <IconButton size="small" onClick={() => openEdit(rec)}>
                    <EditIcon fontSize="small" sx={{ color: "#065F46" }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(rec._id)} disabled={deletingId === rec._id}>
                    {deletingId === rec._id
                      ? <CircularProgress size={16} sx={{ color: "#065F46" }} />
                      : <DeleteIcon fontSize="small" sx={{ color: "#EA001B" }} />}
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo" }}>
                      {rec.pageKey}
                    </Typography>
                    {rec.noIndex && (
                      <Chip label="noindex" size="small" sx={{ bgcolor: "#f59e0b", color: "#fff", fontFamily: "Heebo" }} />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" sx={{ color: "#362B24", opacity: 0.55, fontFamily: "Heebo" }}>
                    {rec.metaTitle ? `${rec.metaTitle.slice(0, 60)}${rec.metaTitle.length > 60 ? "…" : ""}` : "—"}
                  </Typography>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
        {!loading && sorted.length === 0 && (
          <ListItem>
            <ListItemText primary={<Typography sx={{ color: "#362B24", opacity: 0.5, fontFamily: "Heebo" }}>No SEO records yet.</Typography>} />
          </ListItem>
        )}
      </List>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 3 } }}
      >
        <Toolbar disableGutters sx={{ justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
            {editing ? "Edit SEO" : "Add Page SEO"}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon sx={{ color: "#362B24" }} />
          </IconButton>
        </Toolbar>

        {!editing && (
          <TextField
            label="Page Key"
            value={form.pageKey}
            onChange={(e) => handleChange("pageKey", e.target.value.toLowerCase())}
            size="small"
            fullWidth
            required
            helperText="Unique identifier (e.g. homepage, about, contact)"
            sx={{ mb: 2, ...inputSx }}
          />
        )}

        <SeoMetaForm values={form} onChange={handleChange} />

        <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setDrawerOpen(false)}
            sx={{ borderRadius: "28px", height: "41px", px: 3, border: "1px solid #EBEBEB", color: "#362B24", fontFamily: "Heebo" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!form.pageKey}
            sx={{
              borderRadius: "28px",
              height: "41px",
              px: 3,
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              color: "#fff",
              fontFamily: "Heebo",
              boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
              "&:hover": { opacity: 0.9 },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            Save
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
